import {
  AlertTriangle,
  Building,
  Calendar,
  CheckCircle2,
  Copy,
  Download,
  Edit,
  FileText,
  Globe,
  Loader2,
  Lock,
  Phone,
  Search,
  Shield,
  User,
} from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WizardLayout } from "@/components/layout/wizard-layout";
import { LOADING_DURATIONS } from "@/config/loadingDurations";
import { ClassificationBadge } from "@/components/ui/classification-badge";
import type { ClassificationType } from "@/types/entity";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { useDeductCredits } from "@/hooks/api/mutate/useDeductCredits";
import { useGeneratePdf } from "@/hooks/api/mutate/useGeneratePdf";
import { useGenerateXlsx } from "@/hooks/api/mutate/useGenerateXlsx";
import { useCheckActionCredits } from "@/hooks/api/query/useCheckActionCredits";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  extractDomainFromEmail,
  fetchIPAddressesFromDomain,
} from "@/services/company-data-service";
import type { ContactData } from "@/types/contact";
import type { DocumenteAnexateData, EntityData } from "@/types/entity";
import { DocumenteAnexateSection } from "@/components/onboarding/formular-notificare/DocumenteAnexateSection";
import { validateDocumenteAnexate } from "@/utils/onboardingValidation";
import { DEFAULT_CONTACT_DATA } from "@/constants/contact";
import { ReferralEvents, trackReferralEvent } from "@/utils/analytics";
import {
  isContactDataComplete,
  isConfirmationsComplete,
} from "@/utils/contactValidation";
import { isReferralSystemEnabled } from "@/utils/featureFlags";
import { generatePDFReport } from "@/utils/generate-report";
import { generateXlsxReport } from "@/utils/generate-xlsx-report";
// import { createReferralLink } from "@/utils/referralValidation";
import { InsufficientCreditsError } from "@/utils/supabaseFunctionsError";
import { POSTHOG_EVENTS } from "../../constants";
import { CREDIT_ACTION_TYPES } from "../../constants/credit";
import { useWizardSession } from "../../hooks/useWizardSession";
import { cn } from "../../lib/utils";
import { PaywallDialog } from "../PaywallDialog";
import { ReferralSuccessModal } from "../referral/ReferralSuccessModal";

export type ReportData = {
  entityData: EntityData;
  contactData: ContactData;
  documenteAnexate: DocumenteAnexateData | null;
  companyEmail: string;
  companyTelefon: string;
  generatedAt: string;
  reportId: string;
  referralCode: string | null;
};

type Step5ReportGenerationProps = {
  entityData: EntityData;
  onNext: (reportData: ReportData) => void;
  onBack: () => void;
  onStepClick?: (stepIndex: number) => void;
  currentStep?: number;
  steps?: string[];
  userAvatar?: React.ReactNode;
};

const defaultContactData: ContactData = {
  ...DEFAULT_CONTACT_DATA,
  reprezentantLegal: {
    ...DEFAULT_CONTACT_DATA.reprezentantLegal,
    functie: "Director General",
  },
  responsabilSecuritate: {
    ...DEFAULT_CONTACT_DATA.responsabilSecuritate,
    functie: "Responsabil Securitate IT",
  },
  contactOperational: {
    ...DEFAULT_CONTACT_DATA.contactOperational,
    functie: "Manager Operațiuni",
  },
};

function createAdreseIpRowIds(count: number): string[] {
  return Array.from({ length: count }, () => crypto.randomUUID());
}

export function Step5ReportGeneration({
  entityData,
  onNext,
  onBack,
  onStepClick,
  currentStep = 5,
  steps = [],
  userAvatar,
}: Step5ReportGenerationProps) {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [contactData, setContactData] = useState<ContactData>(
    entityData.contact_data || defaultContactData
  );
  const [adreseIpRowIds, setAdreseIpRowIds] = useState<string[]>(() =>
    createAdreseIpRowIds(
      (entityData.contact_data || defaultContactData).adreseIP.length
    )
  );
  const [documenteAnexate, setDocumenteAnexate] =
    useState<DocumenteAnexateData | null>(entityData.documente_anexate ?? null);
  const [companyEmail, setCompanyEmail] = useState(entityData.email || "");
  const [companyTelefon, setCompanyTelefon] = useState(
    entityData.telefon || ""
  );
  const { sessionId } = useWizardSession();

  const { data: creditCheck, isLoading: isCheckingCredits } =
    useCheckActionCredits(
      {
        session_id: sessionId || "",
        action_type: CREDIT_ACTION_TYPES.DOC_NOTIFICARE_DNSC_PDF,
      },
      !!sessionId
    );

  const { data: notificareCreditCheck } = useCheckActionCredits(
    {
      session_id: sessionId || "",
      action_type: CREDIT_ACTION_TYPES.NOTIFICARE_INITIALA_DNSC,
    },
    !!sessionId
  );

  const { mutateAsync: deductCredits } = useDeductCredits();

  const [isGenerating, setIsGenerating] = useState(false);
  const [reportGenerated, setReportGenerated] = useState(() => {
    const cd = entityData.contact_data || defaultContactData;
    return isContactDataComplete(cd) && isConfirmationsComplete(cd);
  });
  const [referralCode, setReferralCode] = useState<string>("");
  const [showReferralModal, setShowReferralModal] = useState(false);

  const { user } = useAuth();
  const [isLoadingIPs, setIsLoadingIPs] = useState(false);
  // const [referralLink, setReferralLink] = useState("");
  // const [isSharing, setIsSharing] = useState(false);

  const { toast } = useToast();

  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallActionType, setPaywallActionType] = useState<
    "notificare_initiala_dnsc" | "doc_notificare_dnsc_pdf"
  >("doc_notificare_dnsc_pdf");

  // PDF generation mutation
  const generatePdfMutation = useGeneratePdf();

  // XLSX generation mutation
  const generateXlsxMutation = useGenerateXlsx();

  // Credit check helper variables
  const isLoadingCredits =
    isCheckingCredits ||
    generatePdfMutation.isPending ||
    generateXlsxMutation.isPending;
  const hasInsufficientCredits =
    creditCheck && !creditCheck.has_credits && !creditCheck.already_purchased;

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});

  const step5FieldId = useId();
  const step5Fid = (suffix: string) => `${step5FieldId}-${suffix}`;

  const handleInputChange = (
    section: keyof ContactData | "company",
    field: string,
    value: any
  ) => {
    // Clear error for this field when user starts typing
    const errorKey = field ? `${section}.${field}` : section;
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[errorKey];
      return newErrors;
    });

    if (section === "company") {
      if (field === "email") setCompanyEmail(value);
      if (field === "telefon") setCompanyTelefon(value);
      return;
    }

    if (section === "adreseIP") {
      const nextIps = Array.isArray(value) ? value : [value];
      setContactData((prev) => ({
        ...prev,
        adreseIP: nextIps,
      }));
      setAdreseIpRowIds(createAdreseIpRowIds(nextIps.length));
    } else if (
      typeof contactData[section] === "object" &&
      contactData[section] !== null
    ) {
      setContactData((prev) => ({
        ...prev,
        [section]: {
          ...(prev[section] as any),
          [field]: value,
        },
      }));
    } else {
      setContactData((prev) => ({
        ...prev,
        [section]: value,
      }));
    }
  };

  const addIPAddress = () => {
    setContactData((prev) => ({
      ...prev,
      adreseIP: [...prev.adreseIP, ""],
    }));
  };

  const removeIPAddress = (index: number) => {
    setContactData((prev) => ({
      ...prev,
      adreseIP: prev.adreseIP.filter((_, i) => i !== index),
    }));
  };

  const updateIPAddress = (index: number, value: string) => {
    setContactData((prev) => ({
      ...prev,
      adreseIP: prev.adreseIP.map((ip, i) => (i === index ? value : ip)),
    }));
  };

  // Copy functions for prefilling forms
  const copyFromReprezentantLegal = (
    targetSection: "responsabilSecuritate" | "contactOperational"
  ) => {
    const source = contactData.reprezentantLegal;
    if (!source.nume || !source.prenume) return;

    const defaultFunctions = {
      responsabilSecuritate: "Responsabil Securitate IT",
      contactOperational: "Manager Operațiuni",
    };

    setContactData((prev) => ({
      ...prev,
      [targetSection]: {
        nume: source.nume,
        prenume: source.prenume,
        functie: defaultFunctions[targetSection],
        telefon: source.telefon,
        email: source.email,
      },
    }));
  };

  const copyFromResponsabilSecuritate = () => {
    const source = contactData.responsabilSecuritate;
    if (!source.nume || !source.prenume) return;

    setContactData((prev) => ({
      ...prev,
      contactOperational: {
        nume: source.nume,
        prenume: source.prenume,
        functie: "Contact Tehnic 24/7",
        telefon: source.telefon,
        email: source.email,
      },
    }));
  };

  const canCopyFromReprezentant = () => {
    return (
      contactData.reprezentantLegal.nume &&
      contactData.reprezentantLegal.prenume
    );
  };

  const canCopyFromResponsabil = () => {
    return (
      contactData.responsabilSecuritate.nume &&
      contactData.responsabilSecuritate.prenume
    );
  };

  // Auto-detect IP addresses from legal representative email
  const autoDetectIPAddresses = async () => {
    const email = contactData.reprezentantLegal.email;
    if (!email) {
      toast({
        title: "Email lipsă",
        description:
          "Vă rugăm să introduceți email-ul reprezentantului legal mai întâi.",
        variant: "destructive",
      });
      return;
    }

    const domain = extractDomainFromEmail(email);
    if (!domain) {
      toast({
        title: "Email invalid",
        description: "Formatul email-ului nu este valid.",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingIPs(true);
    try {
      const ipAddresses = await fetchIPAddressesFromDomain(domain);

      if (ipAddresses.length > 0) {
        setContactData((prev) => ({
          ...prev,
          adreseIP: ipAddresses,
        }));
        setAdreseIpRowIds(createAdreseIpRowIds(ipAddresses.length));

        toast({
          title: "IP-uri detectate",
          description: `S-au găsit ${ipAddresses.length} adrese IP pentru domeniul ${domain}`,
        });
      } else {
        toast({
          title: "Niciun IP găsit",
          description: `Nu s-au găsit adrese IP pentru domeniul ${domain}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching IP addresses:", error);
      toast({
        title: "Eroare la detectarea IP-urilor",
        description:
          error instanceof Error ? error.message : "Eroare neașteptată",
        variant: "destructive",
      });
    } finally {
      setIsLoadingIPs(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Validate Company Email & Telefon
    if (!companyEmail.trim()) {
      newErrors["company.email"] = "Email-ul companiei este obligatoriu";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyEmail)) {
      newErrors["company.email"] = "Email-ul companiei nu este valid";
    }
    if (!companyTelefon.trim()) {
      newErrors["company.telefon"] = "Telefonul companiei este obligatoriu";
    }

    // Validate Reprezentant Legal
    if (!contactData.reprezentantLegal.nume.trim()) {
      newErrors["reprezentantLegal.nume"] = "Numele este obligatoriu";
    }
    if (!contactData.reprezentantLegal.prenume.trim()) {
      newErrors["reprezentantLegal.prenume"] = "Prenumele este obligatoriu";
    }
    if (!contactData.reprezentantLegal.email.trim()) {
      newErrors["reprezentantLegal.email"] = "Email-ul este obligatoriu";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactData.reprezentantLegal.email)
    ) {
      newErrors["reprezentantLegal.email"] = "Email-ul nu este valid";
    }

    // Validate Responsabil Securitate
    if (!contactData.responsabilSecuritate.nume.trim()) {
      newErrors["responsabilSecuritate.nume"] = "Numele este obligatoriu";
    }
    if (!contactData.responsabilSecuritate.prenume.trim()) {
      newErrors["responsabilSecuritate.prenume"] = "Prenumele este obligatoriu";
    }
    if (!contactData.responsabilSecuritate.email.trim()) {
      newErrors["responsabilSecuritate.email"] = "Email-ul este obligatoriu";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        contactData.responsabilSecuritate.email
      )
    ) {
      newErrors["responsabilSecuritate.email"] = "Email-ul nu este valid";
    }

    // Validate Contact Operational
    if (!contactData.contactOperational.nume.trim()) {
      newErrors["contactOperational.nume"] = "Numele este obligatoriu";
    }
    if (!contactData.contactOperational.prenume.trim()) {
      newErrors["contactOperational.prenume"] = "Prenumele este obligatoriu";
    }
    if (!contactData.contactOperational.email.trim()) {
      newErrors["contactOperational.email"] = "Email-ul este obligatoriu";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactData.contactOperational.email)
    ) {
      newErrors["contactOperational.email"] = "Email-ul nu este valid";
    }

    // Validate checkboxes
    if (!contactData.confirmaExactitate) {
      newErrors.confirmaExactitate =
        "Trebuie să confirmați exactitatea informațiilor";
    }

    if (!contactData.acceptaObligatii) {
      newErrors.acceptaObligatii =
        "Trebuie să acceptați obligațiile de conformitate";
    }

    // Validate Documente Anexate
    Object.assign(newErrors, validateDocumenteAnexate(documenteAnexate));

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateReferralCode = async (): Promise<string | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase.functions.invoke(
        "api/generate-referral-code"
      );

      if (error) {
        console.error("Error generating referral code:", error);
        return null;
      }

      return data.code;
    } catch (error) {
      console.error("Error calling generate-referral-code function:", error);
      return null;
    }
  };

  const generateReport = async () => {
    if (!validateForm()) {
      return;
    }

    if (!sessionId) {
      toast({
        title: "Eroare",
        description: "Nu există o sesiune activă",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);

    // Charge notificare_initiala_dnsc on Genereaz[a] Raport DNSC click
    if (!notificareCreditCheck?.already_purchased) {
      if (
        !user ||
        (notificareCreditCheck && !notificareCreditCheck.has_credits)
      ) {
        setIsGenerating(false);
        setPaywallActionType("notificare_initiala_dnsc");
        setShowPaywall(true);
        return;
      }
      try {
        await deductCredits({
          action_type: CREDIT_ACTION_TYPES.NOTIFICARE_INITIALA_DNSC,
          session_id: sessionId,
        });
      } catch (error) {
        setIsGenerating(false);
        if (error instanceof InsufficientCreditsError) {
          setPaywallActionType("notificare_initiala_dnsc");
          setShowPaywall(true);
          return;
        }
        toast({
          title: "Eroare",
          description: "Nu s-au putut deduce creditele.",
          variant: "destructive",
        });
        return;
      }
    }

    try {
      // Simulate report generation
      await new Promise((resolve) =>
        setTimeout(resolve, LOADING_DURATIONS.reportGen)
      );

      // Track report completion
      if (user) {
        trackReferralEvent({
          event: ReferralEvents.REPORT_COMPLETED,
          userId: user.id,
        });
      }

      // Check if referral system is enabled and generate referral code
      let generatedReferralCode = null;
      if (user && (await isReferralSystemEnabled())) {
        generatedReferralCode = await generateReferralCode();
        if (generatedReferralCode) {
          setReferralCode(generatedReferralCode);
          // setReferralLink(createReferralLink(generatedReferralCode));
          trackReferralEvent({
            event: ReferralEvents.CODE_GENERATED,
            userId: user.id,
            referralCode: generatedReferralCode,
          });
        }
      }

      setIsGenerating(false);
      setReportGenerated(true);

      // Prepare final report data
      const reportData: ReportData = {
        entityData,
        contactData,
        documenteAnexate,
        companyEmail,
        companyTelefon,
        generatedAt: new Date().toISOString(),
        reportId: `NIS2-${entityData.cui}-${Date.now()}`,
        referralCode: generatedReferralCode,
      };

      // Show referral modal if code was generated
      if (generatedReferralCode) {
        setShowReferralModal(true);
      }

      // Pass data to parent component
      onNext(reportData);
    } catch (error) {
      console.error("Error generating report:", error);
      toast({
        title: "Eroare",
        description:
          "A apărut o eroare la generarea raportului. Încercați din nou.",
        variant: "destructive",
      });
      setIsGenerating(false);
    }
  };

  // const shareOnLinkedIn = () => {
  // 	setIsSharing(true);
  // 	posthog.capture(POSTHOG_EVENTS.STEP_5_POST_LINKEDIN);
  // 	const message = `Am folosit platforma NIS2.ro pentru clasificarea entității noastre conform directivei NIS 2. Recomand această platformă profesională pentru evaluarea conformității! ${referralLink}`;
  // 	const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(message)}`;
  // 	window.open(linkedInUrl, "_blank");

  // 	setTimeout(() => setIsSharing(false), 2000);
  // };

  // const copyReferralLink = async () => {
  // 	try {
  // 		await navigator.clipboard.writeText(referralLink);
  // 		toast({
  // 			title: "Link copiat!",
  // 			description: "Link-ul de recomandare a fost copiat în clipboard.",
  // 		});
  // 	} catch (error) {
  // 		toast({
  // 			title: "Eroare",
  // 			description: "Nu s-a putut copia link-ul în clipboard.",
  // 			variant: "destructive",
  // 		});
  // 	}
  // };

  const downloadRaportPDF = () => {
    generatePdfMutation.mutate(
      { sessionId, type: "nis2" },
      {
        onSuccess: async (data) => {
          await generatePDFReport(
            data as unknown as {
              template: unknown[];
              inputs: unknown[];
              type: "merge" | undefined;
            }
          );
        },
        onError: (error) => {
          if (error instanceof InsufficientCreditsError) {
            setPaywallActionType("doc_notificare_dnsc_pdf");
            setShowPaywall(true);
            return;
          }
          toast({
            title: "Eroare",
            description: "PDF-ul nu a putut fi generat. Încercați din nou.",
            variant: "destructive",
          });
        },
      }
    );
  };

  const downloadRaportXLSX = () => {
    generateXlsxMutation.mutate(
      { sessionId },
      {
        onSuccess: async (data) => {
          try {
            await generateXlsxReport(data.entityData, contactData);
          } catch {
            toast({
              title: "Eroare",
              description: "XLSX-ul nu a putut fi generat. Încercați din nou.",
              variant: "destructive",
            });
          }
        },
        onError: (error) => {
          if (error instanceof InsufficientCreditsError) {
            setPaywallActionType("doc_notificare_dnsc_pdf");
            setShowPaywall(true);
            return;
          }
          toast({
            title: "Eroare",
            description: "XLSX-ul nu a putut fi generat. Încercați din nou.",
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleEditContactData = () => {
    setReportGenerated(false);
  };

  // Move Dialog outside conditional return
  return (
    <>
      {reportGenerated ? (
        <WizardLayout
          title="Raport DNSC Generat cu Succes"
          description="Raportul oficial pentru DNSC a fost generat și este gata pentru download sau trimitere."
          currentStep={currentStep}
          steps={steps}
          onStepClick={onStepClick}
          userAvatar={userAvatar}
        >
          <div className="max-w-4xl mx-auto space-y-6">
            <Card className="border-success/20">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="md:text-2xl text-xl flex items-center gap-3 text-success">
                    <CheckCircle2 className="h-6 md:h-8 w-6 md:w-8" />
                    Raport Completat
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEditContactData}
                    className="gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Editează
                  </Button>
                </div>
                <CardDescription className="text-sm md:text-base">
                  Raportul NIS 2 a fost generat conform cerințelor DNSC
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 text-sm md:text-base pt-0 md:pt-0">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex items-center gap-3">
                    <Building className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">{entityData.nume_entitate}</p>
                      <p className="text-sm text-muted-foreground">
                        CUI: <span className="font-mono">{entityData.cui}</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-accent" />
                    <ClassificationBadge
                      classification={
                        entityData.nis_classification?.classificationV2 as
                        | ClassificationType
                        | undefined
                      }
                      className="text-sm md:text-lg md:px-4 md:py-2"
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Data generare</p>
                      <p className="text-sm text-muted-foreground font-mono">
                        {new Date().toLocaleDateString("ro-RO")}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h4 className="font-heading font-semibold">
                    Acțiuni disponibile:
                  </h4>
                  <div className="grid gap-3 md:grid-cols-2">
                    <Button
                      className="h-auto p-3 md:p-4 justify-start"
                      onClick={() => {
                        if (hasInsufficientCredits) {
                          setShowPaywall(true);
                        } else {
                          downloadRaportPDF();
                        }
                      }}
                      disabled={isLoadingCredits}
                    >
                      {isLoadingCredits ? (
                        <Loader2 className="h-5 w-5 mr-3 animate-spin" />
                      ) : hasInsufficientCredits ? (
                        <Lock className="h-5 w-5 mr-3" />
                      ) : (
                        <Download className="h-5 w-5 mr-3" />
                      )}
                      <div className="text-left">
                        <p className="font-medium">
                          {generatePdfMutation.isPending
                            ? "Se generează..."
                            : "Download Raport PDF"}
                        </p>
                        <p className="text-xs text-white">
                          Salvează raportul local
                        </p>
                      </div>
                    </Button>

                    <Button
                      className="h-auto p-3 md:p-4 justify-start"
                      variant="outline"
                      onClick={() => {
                        if (hasInsufficientCredits) {
                          setShowPaywall(true);
                        } else {
                          downloadRaportXLSX();
                        }
                      }}
                      disabled={isLoadingCredits}
                    >
                      {isLoadingCredits && generateXlsxMutation.isPending ? (
                        <Loader2 className="h-5 w-5 mr-3 animate-spin" />
                      ) : hasInsufficientCredits ? (
                        <Lock className="h-5 w-5 mr-3" />
                      ) : (
                        <Download className="h-5 w-5 mr-3" />
                      )}
                      <div className="text-left">
                        <p className="font-medium">
                          {generateXlsxMutation.isPending
                            ? "Se generează..."
                            : "Download Raport XLSX"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Instrument NIS2 în format Excel
                        </p>
                      </div>
                    </Button>
                  </div>
                </div>

                <div className="bg-ds-surface-100 rounded-lg p-4">
                  <h5 className="font-heading font-medium text-navy-900 mb-2">
                    Pașii următori:
                  </h5>
                  <ol className="text-sm space-y-1 text-ds-text-600">
                    <li>
                      {" "}
                      1. Descarcați raportul în format PDF și semnați-l digital.
                    </li>
                    <li>
                      2. Pregătiți documentele care atestă dimensiunea companiei
                      (Bilanț, extras Regis(fost Revisal))
                    </li>
                    <li>
                      {" "}
                      3. Trimiteți PDF-ul generat impreună cu documentele de la
                      pasul 2 către evidenta@dnsc.ro
                    </li>

                    <li>
                      4.Pregătițivă pentru etapa 2, cea a analizei de risc si de
                      maturitate cu ajutorul platformei noastre
                    </li>
                  </ol>
                </div>

                <Button
                  onClick={() => navigate("/dashboard")}
                  className="w-full mt-4"
                >
                  Înapoi la Dashboard
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Paywall Dialog */}
          <PaywallDialog
            open={showPaywall}
            onOpenChange={setShowPaywall}
            actionType={paywallActionType}
            onPurchase={(packageId) => {
              console.log("💳 [PAYWALL] User selected package:", packageId);
              toast({
                title: "Redirecționare către plată",
                description: `Vei fi redirecționat către Stripe pentru pachetul ${packageId}`,
              });
              // In production: redirect to Stripe
            }}
          />
        </WizardLayout>
      ) : (
        <WizardLayout
          title="Generare Raport DNSC"
          description="Completați datele lipsă pentru a genera raportul oficial de classificare NIS 2."
          currentStep={currentStep}
          steps={steps}
          onBack={onBack}
          onStepClick={onStepClick}
          showBackButton
          userAvatar={userAvatar}
          footer={
            <div className="ml-auto flex items-center gap-3">
              <Button
                variant="outline"
                onClick={onBack}
                className="min-h-[44px]"
              >
                Înapoi
              </Button>
              <Button
                onClick={generateReport}
                disabled={isGenerating}
                className="min-h-[44px]"
              >
                {isGenerating ? (
                  <>
                    <div className="h-4 w-4 border-2 border-background border-t-transparent rounded-full animate-spin mr-2" />
                    Generez raportul...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Generează Raport DNSC
                  </>
                )}
              </Button>
            </div>
          }
        >
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Summary Card */}
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Sumar Clasificare
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-6 md:pt-0 md:pb-6">
                <div className="flex flex-wrap items-center gap-4">
                  <div>
                    <p className="text-sm md:text-base font-medium">
                      {entityData.nume_entitate}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      CUI: <span className="font-mono">{entityData.cui}</span>
                    </p>
                  </div>
                  <div className="ml-auto">
                    <ClassificationBadge
                      classification={
                        entityData.nis_classification?.classificationV2 as
                        | ClassificationType
                        | undefined
                      }
                      className="text-sm md:text-lg md:px-4 md:py-2"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Date Companie */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Date Companie
                </CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  Email și telefon companie
                </CardDescription>
              </CardHeader>
              <CardContent
                className={`grid gap-4 ${isMobile ? "grid-cols-1" : "md:grid-cols-2"} pt-0 pb-6 md:pt-0 md:pb-6`}
              >
                <div>
                  <Label
                    htmlFor={step5Fid("company-email")}
                    className="text-xs md:text-sm"
                  >
                    Email *
                  </Label>
                  <Input
                    id={step5Fid("company-email")}
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={companyEmail}
                    onChange={(e) =>
                      handleInputChange("company", "email", e.target.value)
                    }
                    placeholder="office@company.ro"
                    className={cn(
                      errors["company.email"] ? "border-destructive" : "",
                      "text-xs md:text-sm"
                    )}
                  />
                  {errors["company.email"] && (
                    <p className="text-sm text-destructive mt-1">
                      {errors["company.email"]}
                    </p>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor={step5Fid("company-telefon")}
                    className="text-xs md:text-sm"
                  >
                    Telefon *
                  </Label>
                  <Input
                    id={step5Fid("company-telefon")}
                    name="tel"
                    autoComplete="tel"
                    value={companyTelefon}
                    onChange={(e) =>
                      handleInputChange("company", "telefon", e.target.value)
                    }
                    placeholder="+40..."
                    className={cn(
                      errors["company.telefon"] ? "border-destructive" : "",
                      "text-xs md:text-sm"
                    )}
                  />
                  {errors["company.telefon"] && (
                    <p className="text-sm text-destructive mt-1">
                      {errors["company.telefon"]}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Error Banner */}
            {Object.keys(errors).length > 0 && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4">
                <div
                  className={`flex ${isMobile ? "flex-col" : "items-center"} gap-2`}
                >
                  <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
                  <span
                    className={`text-sm font-medium text-destructive ${isMobile ? "leading-relaxed" : ""}`}
                  >
                    Există {Object.keys(errors).length}{" "}
                    {Object.keys(errors).length === 1 ? "eroare" : "erori"} de
                    validare. Vă rugăm să corectați câmpurile marcate.
                  </span>
                </div>
              </div>
            )}

            {/* Reprezentant Legal */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Reprezentant Legal
                </CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  Persoana autorizată să semneze raportul către DNSC
                </CardDescription>
              </CardHeader>
              <CardContent
                className={`grid gap-4 ${isMobile ? "grid-cols-1" : "md:grid-cols-2"} pt-0 pb-6 md:pt-0 md:pb-6`}
              >
                <div>
                  <Label
                    htmlFor={step5Fid("repr-nume")}
                    className="text-xs md:text-sm"
                  >
                    Nume *
                  </Label>
                  <Input
                    id={step5Fid("repr-nume")}
                    name="family-name"
                    autoComplete="family-name"
                    value={contactData.reprezentantLegal.nume}
                    onChange={(e) =>
                      handleInputChange(
                        "reprezentantLegal",
                        "nume",
                        e.target.value
                      )
                    }
                    placeholder="Numele reprezentantului legal"
                    className={cn(
                      errors["reprezentantLegal.nume"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                  {errors["reprezentantLegal.nume"] && (
                    <p className="text-sm text-destructive mt-1">
                      {errors["reprezentantLegal.nume"]}
                    </p>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor={step5Fid("repr-prenume")}
                    className="text-xs md:text-sm"
                  >
                    Prenume *
                  </Label>
                  <Input
                    id={step5Fid("repr-prenume")}
                    name="given-name"
                    autoComplete="given-name"
                    value={contactData.reprezentantLegal.prenume}
                    onChange={(e) =>
                      handleInputChange(
                        "reprezentantLegal",
                        "prenume",
                        e.target.value
                      )
                    }
                    placeholder="Prenumele reprezentantului legal"
                    className={cn(
                      errors["reprezentantLegal.prenume"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                  {errors["reprezentantLegal.prenume"] && (
                    <p className="text-sm text-destructive mt-1">
                      {errors["reprezentantLegal.prenume"]}
                    </p>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor={step5Fid("repr-functie")}
                    className="text-xs md:text-sm"
                  >
                    Funcție
                  </Label>
                  <Input
                    id={step5Fid("repr-functie")}
                    name="organization-title"
                    autoComplete="organization-title"
                    value={contactData.reprezentantLegal.functie}
                    onChange={(e) =>
                      handleInputChange(
                        "reprezentantLegal",
                        "functie",
                        e.target.value
                      )
                    }
                    placeholder="Director General"
                    className={cn(
                      errors["reprezentantLegal.functie"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                </div>
                <div>
                  <Label
                    htmlFor={step5Fid("repr-telefon")}
                    className="text-xs md:text-sm"
                  >
                    Telefon
                  </Label>
                  <Input
                    id={step5Fid("repr-telefon")}
                    name="tel"
                    autoComplete="tel"
                    value={contactData.reprezentantLegal.telefon}
                    onChange={(e) =>
                      handleInputChange(
                        "reprezentantLegal",
                        "telefon",
                        e.target.value
                      )
                    }
                    placeholder="+40..."
                    className={cn(
                      errors["reprezentantLegal.telefon"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label
                    htmlFor={step5Fid("repr-email")}
                    className="text-xs md:text-sm"
                  >
                    Email *
                  </Label>
                  <Input
                    id={step5Fid("repr-email")}
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={contactData.reprezentantLegal.email}
                    onChange={(e) =>
                      handleInputChange(
                        "reprezentantLegal",
                        "email",
                        e.target.value
                      )
                    }
                    placeholder="reprezentant@company.ro"
                    className={cn(
                      errors["reprezentantLegal.email"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                  {errors["reprezentantLegal.email"] && (
                    <p className="text-sm text-destructive mt-1">
                      {errors["reprezentantLegal.email"]}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Responsabil Securitate */}
            <Card>
              <CardHeader>
                <CardTitle
                  className={`flex items-center ${isMobile ? "flex-col gap-3" : "justify-between"}`}
                >
                  <div className="text-base md:text-lg flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Responsabil Securitate
                  </div>
                  <Button
                    variant="outline"
                    size={isMobile ? "default" : "sm"}
                    onClick={() =>
                      copyFromReprezentantLegal("responsabilSecuritate")
                    }
                    disabled={!canCopyFromReprezentant()}
                    className={`${isMobile ? "w-full text-sm" : "text-xs"} min-h-[44px]`}
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copiază din Reprezentant Legal
                  </Button>
                </CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  Persoana responsabilă cu securitatea rețelelor și sistemelor
                  informaționale
                </CardDescription>
              </CardHeader>
              <CardContent
                className={`grid gap-4 ${isMobile ? "grid-cols-1" : "md:grid-cols-2"} pt-0 pb-6 md:pt-0 md:pb-6`}
              >
                <div>
                  <Label
                    htmlFor={step5Fid("sec-nume")}
                    className="text-xs md:text-sm"
                  >
                    Nume *
                  </Label>
                  <Input
                    id={step5Fid("sec-nume")}
                    name="family-name"
                    autoComplete="family-name"
                    value={contactData.responsabilSecuritate.nume}
                    onChange={(e) =>
                      handleInputChange(
                        "responsabilSecuritate",
                        "nume",
                        e.target.value
                      )
                    }
                    placeholder="Numele responsabilului"
                    className={cn(
                      errors["responsabilSecuritate.nume"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                  {errors["responsabilSecuritate.nume"] && (
                    <p className="text-sm text-destructive mt-1">
                      {errors["responsabilSecuritate.nume"]}
                    </p>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor={step5Fid("sec-prenume")}
                    className="text-xs md:text-sm"
                  >
                    Prenume *
                  </Label>
                  <Input
                    id={step5Fid("sec-prenume")}
                    name="given-name"
                    autoComplete="given-name"
                    value={contactData.responsabilSecuritate.prenume}
                    onChange={(e) =>
                      handleInputChange(
                        "responsabilSecuritate",
                        "prenume",
                        e.target.value
                      )
                    }
                    placeholder="Prenumele responsabilului"
                    className={cn(
                      errors["responsabilSecuritate.prenume"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                  {errors["responsabilSecuritate.prenume"] && (
                    <p className="text-sm text-destructive mt-1">
                      {errors["responsabilSecuritate.prenume"]}
                    </p>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor={step5Fid("sec-functie")}
                    className="text-xs md:text-sm"
                  >
                    Funcție
                  </Label>
                  <Input
                    id={step5Fid("sec-functie")}
                    name="organization-title"
                    autoComplete="organization-title"
                    value={contactData.responsabilSecuritate.functie}
                    onChange={(e) =>
                      handleInputChange(
                        "responsabilSecuritate",
                        "functie",
                        e.target.value
                      )
                    }
                    placeholder="CISO, IT Manager, etc."
                    className={cn(
                      errors["responsabilSecuritate.functie"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                </div>
                <div>
                  <Label
                    htmlFor={step5Fid("sec-telefon")}
                    className="text-xs md:text-sm"
                  >
                    Telefon
                  </Label>
                  <Input
                    id={step5Fid("sec-telefon")}
                    name="tel"
                    autoComplete="tel"
                    value={contactData.responsabilSecuritate.telefon}
                    onChange={(e) =>
                      handleInputChange(
                        "responsabilSecuritate",
                        "telefon",
                        e.target.value
                      )
                    }
                    placeholder="+40..."
                    className={cn(
                      errors["responsabilSecuritate.telefon"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label
                    htmlFor={step5Fid("sec-email")}
                    className="text-xs md:text-sm"
                  >
                    Email *
                  </Label>
                  <Input
                    id={step5Fid("sec-email")}
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={contactData.responsabilSecuritate.email}
                    onChange={(e) =>
                      handleInputChange(
                        "responsabilSecuritate",
                        "email",
                        e.target.value
                      )
                    }
                    placeholder="security@company.ro"
                    className={cn(
                      errors["responsabilSecuritate.email"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                  {errors["responsabilSecuritate.email"] && (
                    <p className="text-sm text-destructive mt-1">
                      {errors["responsabilSecuritate.email"]}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Contact Operațional */}
            <Card>
              <CardHeader>
                <CardTitle
                  className={`flex items-center ${isMobile ? "flex-col gap-3" : "justify-between"}`}
                >
                  <div className="text-base md:text-lg flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    Contact Operațional 24/7
                  </div>
                  <div
                    className={`flex ${isMobile ? "flex-col w-full" : "gap-2"} ${isMobile ? "gap-2" : ""}`}
                  >
                    <Button
                      variant="outline"
                      size={isMobile ? "default" : "sm"}
                      onClick={() =>
                        copyFromReprezentantLegal("contactOperational")
                      }
                      disabled={!canCopyFromReprezentant()}
                      className={`${isMobile ? "w-full text-sm" : "text-xs"} min-h-[44px]`}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copiază din Reprezentant Legal
                    </Button>
                    <Button
                      variant="outline"
                      size={isMobile ? "default" : "sm"}
                      onClick={copyFromResponsabilSecuritate}
                      disabled={!canCopyFromResponsabil()}
                      className={`${isMobile ? "w-full text-sm" : "text-xs"} min-h-[44px]`}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copiază din Responsabil Securitate
                    </Button>
                  </div>
                </CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  Persoana de contact pentru situații de urgență și incidente
                </CardDescription>
              </CardHeader>
              <CardContent
                className={`grid gap-4 ${isMobile ? "grid-cols-1" : "md:grid-cols-2"} pt-0 pb-6 md:pt-0 md:pb-6`}
              >
                <div>
                  <Label
                    htmlFor={step5Fid("op-nume")}
                    className="text-xs md:text-sm"
                  >
                    Nume *
                  </Label>
                  <Input
                    id={step5Fid("op-nume")}
                    name="family-name"
                    autoComplete="family-name"
                    value={contactData.contactOperational.nume}
                    onChange={(e) =>
                      handleInputChange(
                        "contactOperational",
                        "nume",
                        e.target.value
                      )
                    }
                    placeholder="Numele contactului"
                    className={cn(
                      errors["contactOperational.nume"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                  {errors["contactOperational.nume"] && (
                    <p className="text-sm text-destructive mt-1">
                      {errors["contactOperational.nume"]}
                    </p>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor={step5Fid("op-prenume")}
                    className="text-xs md:text-sm"
                  >
                    Prenume *
                  </Label>
                  <Input
                    id={step5Fid("op-prenume")}
                    name="given-name"
                    autoComplete="given-name"
                    value={contactData.contactOperational.prenume}
                    onChange={(e) =>
                      handleInputChange(
                        "contactOperational",
                        "prenume",
                        e.target.value
                      )
                    }
                    placeholder="Prenumele contactului"
                    className={cn(
                      errors["contactOperational.prenume"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                  {errors["contactOperational.prenume"] && (
                    <p className="text-sm text-destructive mt-1">
                      {errors["contactOperational.prenume"]}
                    </p>
                  )}
                </div>
                <div>
                  <Label
                    htmlFor={step5Fid("op-functie")}
                    className="text-xs md:text-sm"
                  >
                    Funcție
                  </Label>
                  <Input
                    id={step5Fid("op-functie")}
                    name="organization-title"
                    autoComplete="organization-title"
                    value={contactData.contactOperational.functie}
                    onChange={(e) =>
                      handleInputChange(
                        "contactOperational",
                        "functie",
                        e.target.value
                      )
                    }
                    placeholder="NOC Manager, DevOps, etc."
                    className={cn(
                      errors["contactOperational.functie"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                </div>
                <div>
                  <Label
                    htmlFor={step5Fid("op-telefon")}
                    className="text-xs md:text-sm"
                  >
                    Telefon urgență
                  </Label>
                  <Input
                    id={step5Fid("op-telefon")}
                    name="tel"
                    autoComplete="tel"
                    value={contactData.contactOperational.telefon}
                    onChange={(e) =>
                      handleInputChange(
                        "contactOperational",
                        "telefon",
                        e.target.value
                      )
                    }
                    placeholder="+40... (disponibil 24/7)"
                    className={cn(
                      errors["contactOperational.telefon"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label
                    htmlFor={step5Fid("op-email")}
                    className="text-xs md:text-sm"
                  >
                    Email *
                  </Label>
                  <Input
                    id={step5Fid("op-email")}
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={contactData.contactOperational.email}
                    onChange={(e) =>
                      handleInputChange(
                        "contactOperational",
                        "email",
                        e.target.value
                      )
                    }
                    placeholder="noc@company.ro"
                    className={cn(
                      errors["contactOperational.email"]
                        ? "border-destructive"
                        : "",
                      "text-xs md:text-sm"
                    )}
                  />
                  {errors["contactOperational.email"] && (
                    <p className="text-sm text-destructive mt-1">
                      {errors["contactOperational.email"]}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Date Tehnice */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Informații Tehnice
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-0 pb-6 md:pt-0 md:pb-6">
                <div>
                  <div
                    className={`flex ${isMobile ? "flex-col gap-2" : "items-center justify-between"} mb-2`}
                  >
                    <Label className="text-xs md:text-sm">
                      Adrese IP Publice
                    </Label>
                    <Button
                      variant="outline"
                      size={isMobile ? "default" : "sm"}
                      onClick={autoDetectIPAddresses}
                      disabled={
                        !contactData.reprezentantLegal.email || isLoadingIPs
                      }
                      className={`${isMobile ? "w-full text-sm" : "text-xs"} min-h-[44px]`}
                    >
                      {isLoadingIPs ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Detectez...
                        </>
                      ) : (
                        <>
                          <Search className="h-3 w-3 mr-1" />
                          Auto-detectare IP
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {contactData.adreseIP.map((ip, index) => (
                      <div
                        key={adreseIpRowIds[index]}
                        className={`flex ${isMobile ? "flex-col" : "gap-2"} ${isMobile ? "gap-2" : ""}`}
                      >
                        <Input
                          value={ip}
                          onChange={(e) =>
                            updateIPAddress(index, e.target.value)
                          }
                          placeholder="Ex: 192.168.1.1"
                          className={isMobile ? "w-full" : ""}
                        />
                        {contactData.adreseIP.length > 1 && (
                          <Button
                            variant="outline"
                            size={isMobile ? "default" : "sm"}
                            onClick={() => removeIPAddress(index)}
                            className={`${isMobile ? "w-full" : ""} min-h-[44px]`}
                          >
                            Șterge
                          </Button>
                        )}
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size={isMobile ? "default" : "sm"}
                        onClick={addIPAddress}
                        className={`${isMobile ? "w-full" : ""} min-h-[44px]`}
                      >
                        Adaugă IP
                      </Button>
                    </div>
                  </div>
                  {!contactData.reprezentantLegal.email && (
                    <p className="text-xs text-muted-foreground mt-1">
                      💡 Introduceți email-ul reprezentantului legal pentru
                      auto-detectarea IP-urilor
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Documente Anexate */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Documente Anexate
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-6 md:pt-0 md:pb-6">
                <DocumenteAnexateSection
                  data={documenteAnexate}
                  onChange={setDocumenteAnexate}
                  fieldErrors={errors}
                />
              </CardContent>
            </Card>

            {/* Confirmări */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-lg">
                  Confirmări Finale
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-0 pb-6 md:pt-0 md:pb-6">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id={step5Fid("confirma-exactitate")}
                    checked={contactData.confirmaExactitate}
                    onCheckedChange={(checked) => {
                      posthog.capture(POSTHOG_EVENTS.STEP_5_CONFIRM_DATA);
                      handleInputChange("confirmaExactitate", "", checked);
                    }}
                    className={
                      errors.confirmaExactitate
                        ? "border-destructive data-[state=checked]:bg-destructive data-[state=checked]:border-destructive"
                        : ""
                    }
                  />
                  <Label
                    htmlFor={step5Fid("confirma-exactitate")}
                    className="text-xs md:text-sm leading-relaxed"
                  >
                    Confirm că toate informațiile furnizate sunt exacte și
                    complete, și că sunt autorizat să reprezint entitatea în
                    relația cu DNSC.
                  </Label>
                </div>
                {errors.confirmaExactitate && (
                  <p className="text-sm text-destructive mt-1 ml-6">
                    {errors.confirmaExactitate}
                  </p>
                )}

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id={step5Fid("accepta-obligatii")}
                    checked={contactData.acceptaObligatii}
                    onCheckedChange={(checked) => {
                      posthog.capture(POSTHOG_EVENTS.STEP_5_ACCEPT_OBLIGATION);
                      handleInputChange("acceptaObligatii", "", checked);
                    }}
                    className={
                      errors.acceptaObligatii
                        ? "border-destructive data-[state=checked]:bg-destructive data-[state=checked]:border-destructive"
                        : ""
                    }
                  />
                  <Label
                    htmlFor={step5Fid("accepta-obligatii")}
                    className="text-xs md:text-sm leading-relaxed"
                  >
                    Accept obligațiile de conformitate NIS 2 și mă angajez să
                    implementez măsurile de securitate necesare conform
                    clasificării primite.
                  </Label>
                </div>
                {errors.acceptaObligatii && (
                  <p className="text-xs md:text-sm text-destructive mt-1 ml-6">
                    {errors.acceptaObligatii}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Referral Success Modal */}
          {showReferralModal && referralCode && (
            <ReferralSuccessModal
              referralCode={referralCode}
              onClose={() => setShowReferralModal(false)}
            />
          )}
        </WizardLayout>
      )}
    </>
  );
}
